import { observer } from 'mobx-react'
import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import { EmptyState, InlinePill, ListContent, ListPage, PageActionRow, PageHeaderRow, Surface } from '../components/DashboardPrimitives'
import Dropdown, { DropdownContext, DropdownItem } from '../components/Dropdown'
import LoadingBar from '../components/LoadingBar'
import PageHeader from '../components/PageHeader'
import { ReportKindGlyph } from '../components/ReportKindGlyph'
import RequestReportDrawer from '../components/RequestReportDrawer'
import SegmentedControl from '../components/SegmentedControl'
import Spinner from '../components/Spinner'
import Radom, {
  ListReportsResponse, ReportFile, ReportFormat, ReportKind, ReportView, StatementMonthView
} from '../state/Radom'
import { WINDOW_SIZE } from '../util/Constants'
import { downloadBlob, errorToast, successToast } from '../util/Util'
import {
  AccountRowButton,
  AccountRowStatic,
  GroupLabel,
  GroupStack,
  GroupSurface,
  RowIconCircle,
  RowMain,
  RowSub,
  RowTitle,
  RowTitleLine,
  focusRing
} from './Money/MoneyStyles'

const POLL_INTERVAL_MS = 5_000
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// ---------------------------------------------------------------------------
// Layout: the same shell as the Transactions page, so the header clears the
// top bar and the content shares its gutters.
// ---------------------------------------------------------------------------

const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 30px 48px;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    padding: 20px 16px;
  }
`

const SectionHead = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 28px;
  padding: 0 4px;
`

// The latest statement gets its own card: one glance, one action.
const Hero = styled(Surface)`
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 20px 22px;
`

const HeroHead = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 320px;
  gap: 16px;
  min-width: 0;
`

const HeroMain = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const HeroEyebrow = styled.span`
  color: var(--text-muted, #6f7888);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  line-height: 1.25;
  text-transform: uppercase;
`

const HeroTitle = styled.span`
  color: var(--ink-strong, #121722);
  font-size: 20px;
  font-weight: 550;
  letter-spacing: -0.01em;
  line-height: 1.2;
`

const HeroSub = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 13px;
  line-height: 1.4;
`

const HeroActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    width: 100%;

    > * {
      flex: 1 1 auto;
    }
  }
`

// A month reads as a calendar leaf: the abbreviation on a soft tile.
const MonthTile = styled.div<{ $size?: number, $muted?: boolean }>`
  align-items: center;
  background: var(--surface-pressed, #eef2f7);
  border-radius: ${({ $size = 40 }) => Math.round($size * 0.3)}px;
  color: ${({ $muted }) => $muted ? 'var(--text-faint, #9aa3b2)' : 'var(--ink, #20242d)'};
  display: flex;
  flex: 0 0 auto;
  font-size: ${({ $size = 40 }) => $size >= 48 ? '12px' : '10.5px'};
  font-weight: 600;
  height: ${({ $size = 40 }) => $size}px;
  justify-content: center;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  width: ${({ $size = 40 }) => $size}px;
`

const Title = styled(RowTitle)`
  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    white-space: normal;
  }
`

const StatusDot = styled.span<{ $tone: 'green' | 'blue' | 'red' | 'neutral' }>`
  background: ${({ $tone }) => ({
    green: 'var(--pill-green-ink, #16724b)',
    blue: 'var(--pill-blue-ink, #3157c6)',
    red: 'var(--pill-red-ink, #c51735)',
    neutral: 'var(--text-faint, #9aa3b2)'
  })[$tone]};
  border-radius: 999px;
  display: inline-block;
  flex: 0 0 auto;
  height: 6px;
  margin-right: 7px;
  transform: translateY(-1px);
  width: 6px;
`

const RowActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
`

// Compact pill for one download: the format is the label, the glyph says what it does.
const Chip = styled.button`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 999px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  gap: 6px;
  height: 30px;
  line-height: 1;
  padding: 0 11px 0 9px;
  transition: background-color 0.14s ease, border-color 0.14s ease, opacity 0.14s ease, transform 0.1s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--surface-hover, #f5f7fb);
    border-color: var(--border-strong, #d6dae2);
  }

  &:active:not(:disabled) {
    transform: translateY(1px) scale(0.98);
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }

  ${focusRing}
`

// Text action for a row that has nothing to download yet.
const RowAction = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 4px;
  line-height: 1.2;
  padding: 6px 8px;
  transition: background-color 0.14s ease, transform 0.1s ease;
  white-space: nowrap;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    transform: translateY(1px);
  }

  ${focusRing}
`

const Preparing = styled.span`
  align-items: center;
  color: var(--text-muted, #6b7280);
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 500;
  gap: 8px;
  padding: 0 4px;
  white-space: nowrap;
`

// A month that has no statement yet: the whole row asks for one.
const MonthRowButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  display: grid;
  font-family: var(--font-sans);
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  padding: 10px 12px;
  text-align: left;
  transition: background-color 0.14s ease;
  width: 100%;

  &:hover {
    background: var(--surface-hover, #f5f7fb);
  }

  &:active {
    background: var(--surface-pressed, #eef1f7);
  }

  &:disabled {
    cursor: default;
  }

  ${focusRing}
`

const RowHint = styled.span`
  align-items: center;
  color: var(--text-muted, #6c7483);
  display: inline-flex;
  font-size: 13px;
  font-weight: 500;
  gap: 4px;
  white-space: nowrap;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    span {
      display: none;
    }
  }
`

// A row with a document: the row itself downloads the PDF, the chips pick a format.
const DocumentRow = styled(AccountRowButton)`
  cursor: pointer;
`

const Highlight = styled.div<{ $on: boolean }>`
  border-radius: 12px;
  box-shadow: ${({ $on }) => $on ? '0 0 0 2px rgba(49, 87, 198, 0.35)' : 'none'};
  transition: box-shadow 0.5s ease;
`

// Up to this many years sit side by side; beyond it the years move into a
// menu, so the control stays one pill wide however old the account gets.
const YEARS_SIDE_BY_SIDE = 3

// Restyles the shared dropdown's trigger into the same pill as the download chips.
const YearShell = styled.div`
  & [role='button'] {
    background: var(--surface, #ffffff);
    border: 1px solid var(--border, #e3e7ef);
    border-radius: 999px;
    column-gap: 7px;
    height: 30px;
    min-height: 30px;
    padding: 0 10px 0 12px;
    transition: background-color 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
  }

  & [role='button']:hover {
    background: var(--surface-hover, #f5f7fb);
    border-color: var(--border-strong, #d6dae2);
  }

  & [role='button'][aria-expanded='true'] {
    border-color: var(--border-strong, #d6dae2);
    box-shadow: 0 0 0 3px var(--focus-ring, rgba(17, 17, 17, 0.05));
  }
`

const YearValue = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1;
`

const YearList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 6px;
`

const YearOption = styled(DropdownItem)<{ $active: boolean }>`
  align-items: center;
  border-radius: 9px;
  color: ${({ $active }) => $active ? 'var(--ink-strong, #10141f)' : 'var(--text-muted, #6f7686)'};
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 10px;
  justify-content: space-between;
  padding: 8px 10px;
`

const CheckGlyph = (): ReactElement => (
  <svg viewBox='0 0 16 16' width='12' height='12' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='m3.5 8.5 3 3 6-7' />
  </svg>
)

interface YearPickerProps { years: string[], year: string, onChange: (year: string) => void }

const YearMenu = ({ years, year, onChange }: YearPickerProps): ReactElement => {
  const { closeDropdown } = useContext(DropdownContext)
  return <YearList role='listbox' aria-label='Year'>
    {years.map(y =>
      <YearOption
        key={y}
        role='option'
        aria-selected={y === year}
        $active={y === year}
        onClick={() => { onChange(y); closeDropdown() }}>
        {y}
        {y === year && <CheckGlyph />}
      </YearOption>
    )}
  </YearList>
}

const YearPicker = ({ years, year, onChange }: YearPickerProps): ReactElement =>
  years.length <= YEARS_SIDE_BY_SIDE
    ? <SegmentedControl tabs={years} selectedTab={year} onTabChange={onChange} />
    : <YearShell data-testid='year-picker'>
      <Dropdown
        preferredAlignment='right'
        innerStyle={{ minWidth: 124 }}
        selectedContent={<YearValue>{year}</YearValue>}
        dropdownContent={<YearMenu years={years} year={year} onChange={onChange} />} />
    </YearShell>

const DownloadGlyph = (): ReactElement => (
  <svg viewBox='0 0 16 16' width='12' height='12' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='M8 2.5v8m0 0 3-3m-3 3-3-3M3 13.5h10' />
  </svg>
)

const ChevronGlyph = (): ReactElement => (
  <svg viewBox='0 0 16 16' width='12' height='12' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='m6 3.5 4.5 4.5L6 12.5' />
  </svg>
)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const monthAbbrev = (periodKey: string): string => MONTHS_SHORT[Number(periodKey.slice(5, 7)) - 1] ?? ''

const shortDate = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`
}

const shortDateYear = (iso: string): string => `${shortDate(iso)} ${new Date(iso).getFullYear()}`

const isInFlight = (r?: ReportView | null): boolean => r?.status === 'queued' || r?.status === 'running'

// A refused document is ours to fix; asking again would only repeat the refusal,
// so the page says so and offers nothing to press.
const isRefused = (r?: ReportView | null): boolean => r?.status === 'failed' && r.failureCode === 'inconsistent'

const retryLabel = (r: ReportView): string => r.failureCode === 'too_large' ? 'Shorter period' : 'Try again'

const formatLabel = (format: ReportFormat): string => format.toUpperCase()

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface StatusLine { tone: 'green' | 'blue' | 'red' | 'neutral', text: string }

const statementStatus = (report?: ReportView | null): StatusLine => {
  if (!report) return { tone: 'neutral', text: 'Available on request' }
  switch (report.status) {
  case 'ready':
    return { tone: 'green', text: `Ready${report.completedAt ? ` · prepared ${shortDate(report.completedAt)}` : ''}` }
  case 'queued':
  case 'running':
    return { tone: 'blue', text: 'Preparing, usually under a minute' }
  case 'failed':
    return { tone: 'red', text: report.failureMessage ?? 'Could not be prepared' }
  case 'expired':
    return { tone: 'neutral', text: 'Available on request' }
  }
}

const reportStatus = (report: ReportView): StatusLine => {
  switch (report.status) {
  case 'ready': {
    // A PDF asked for but not produced means the table was too long for one.
    const pdfLeftOut = report.formats.includes('pdf') && !report.files.some(f => f.format === 'pdf')
    return {
      tone: 'green',
      text: `Ready${report.expiresAt ? ` until ${shortDateYear(report.expiresAt)}` : ''}` +
        `${report.isTestMode ? ' · test mode' : ''}${pdfLeftOut ? ' · too long for a PDF, CSV only' : ''}`
    }
  }
  case 'queued':
  case 'running':
    return { tone: 'blue', text: 'Preparing, this can take a few minutes for long periods' }
  case 'failed':
    return { tone: 'red', text: report.failureMessage ?? 'Could not be prepared' }
  case 'expired':
    return { tone: 'neutral', text: 'Expired · request it again if you still need it' }
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Statements = observer((): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<ListReportsResponse>()
  const [loading, setLoading] = useState(true)
  const [requestOpen, setRequestOpen] = useState(searchParams.get('request') !== null)
  const [initialKind, setInitialKind] = useState<ReportKind | undefined>(
    (searchParams.get('request') as ReportKind) || undefined
  )
  const [busy, setBusy] = useState<string | null>(null)
  const [preparing, setPreparing] = useState<string | null>(null)
  const [year, setYear] = useState<string>()
  const openId = searchParams.get('open')
  const seenRef = useRef<Set<string>>(new Set())
  // A statement the person asked for from its row downloads itself once ready.
  const autoDownloadRef = useRef<string | null>(null)

  const allReports = useCallback((d?: ListReportsResponse): ReportView[] =>
    d ? [...d.reports, ...d.statements.map(s => s.report).filter(Boolean) as ReportView[]] : [], [])

  const load = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setLoading(true)
    try {
      const res = await Radom.listReports()
      setData(res)
      Radom.reportsUnreadCount = res.unreadCount
    } catch (err: any) {
      if (!silent) errorToast(err?.response?.data?.error ?? 'Statements could not be loaded')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const inFlight = useMemo(() => allReports(data).some(isInFlight), [data, allReports])

  useEffect(() => {
    if (!inFlight) return
    const interval = setInterval(() => { void load(true) }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [inFlight, load])

  // Opening the page is reading the inbox: new documents stop counting as new.
  useEffect(() => {
    const unseen = allReports(data).filter(r => r.isNew && !seenRef.current.has(r.id)).map(r => r.id)
    if (unseen.length === 0) return
    unseen.forEach(id => seenRef.current.add(id))
    const timer = setTimeout(() => { void Radom.markReportsSeen(unseen).catch(() => {}) }, 2_500)
    return () => clearTimeout(timer)
  }, [data, allReports])

  const years = useMemo(() => {
    const set = new Set((data?.statements ?? []).map(s => s.periodKey.slice(0, 4)))
    return [...set].sort((a, b) => b.localeCompare(a))
  }, [data])

  useEffect(() => {
    if (!year && years.length > 0) setYear(years[0])
  }, [years, year])

  // A link from an e-mail lands on the document it is about.
  useEffect(() => {
    if (!openId || !data) return
    const target = allReports(data).find(r => r.id === openId)
    if (!target) return
    if (target.periodKey) setYear(target.periodKey.slice(0, 4))
    const timer = setTimeout(() => {
      document.getElementById(`report-${openId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 150)
    return () => clearTimeout(timer)
  }, [openId, data, allReports])

  const download = useCallback(async (report: ReportView, format: ReportFormat): Promise<void> => {
    setBusy(report.id)
    try {
      const { blob, fileName } = await Radom.downloadReport(report.id, format)
      downloadBlob(blob, fileName)
    } catch (err: any) {
      let message = 'The file could not be downloaded'
      try {
        const text = await (err?.response?.data as Blob | undefined)?.text?.()
        message = text ? (JSON.parse(text).error ?? message) : message
      } catch {}
      errorToast(message)
    } finally {
      setBusy(null)
    }
  }, [])

  // The statement someone asked for from its row arrives as a download.
  useEffect(() => {
    const id = autoDownloadRef.current
    if (!id || !data) return
    const report = allReports(data).find(r => r.id === id)
    if (!report || isInFlight(report)) return
    autoDownloadRef.current = null
    if (report.status === 'ready' && report.files.some(f => f.format === 'pdf')) {
      successToast(`Your ${report.periodLabel} statement is ready`)
      void download(report, 'pdf')
    }
  }, [data, allReports, download])

  const prepareMonth = async (month: StatementMonthView): Promise<void> => {
    setPreparing(month.periodKey)
    try {
      const [y, m] = month.periodKey.split('-').map(Number)
      const created = await Radom.createReport({
        kind: 'statement',
        periodStart: new Date(Date.UTC(y, m - 1, 1)).toISOString(),
        periodEnd: new Date(Date.UTC(y, m, 1)).toISOString(),
        formats: ['pdf', 'csv'],
        notifyByEmail: false
      })
      autoDownloadRef.current = created.id
      setData(d => d
        ? { ...d, statements: d.statements.map(s => s.periodKey === month.periodKey ? { ...s, report: created } : s) }
        : d)
    } catch (err: any) {
      errorToast(err?.response?.data?.error ?? 'The statement could not be prepared')
    } finally {
      setPreparing(null)
    }
  }

  const openRequest = (kind?: ReportKind): void => {
    setInitialKind(kind)
    setRequestOpen(true)
  }

  const closeRequest = (): void => {
    setRequestOpen(false)
    if (searchParams.has('request')) {
      searchParams.delete('request')
      setSearchParams(searchParams, { replace: true })
    }
  }

  const onCreated = (report: ReportView): void => {
    closeRequest()
    successToast(report.status === 'ready'
      ? 'That one was already prepared and is ready to download'
      : 'We are preparing your report. It will appear here when it is ready')
    if (report.periodKey) setYear(report.periodKey.slice(0, 4))
    void load(true)
  }

  // -------------------------------------------------------------------------
  // Pieces
  // -------------------------------------------------------------------------

  // PDF first: it is the document, the CSV is the data behind it.
  const orderedFiles = (report: ReportView): ReportFile[] =>
    [...report.files].sort((a, b) => (a.format === 'pdf' ? 0 : 1) - (b.format === 'pdf' ? 0 : 1))

  const downloadChips = (report: ReportView): ReactElement => (
    <RowActions>
      {orderedFiles(report).map(file =>
        <Chip
          key={file.format}
          type='button'
          disabled={busy === report.id}
          title={`${file.fileName} · ${formatBytes(file.byteSize)}`}
          aria-label={`Download ${report.title} for ${report.periodLabel} as ${formatLabel(file.format)}`}
          onClick={e => { e.stopPropagation(); void download(report, file.format) }}>
          {busy === report.id ? <Spinner style={{ width: 12, height: 12 }} /> : <DownloadGlyph />}
          {formatLabel(file.format)}
        </Chip>
      )}
    </RowActions>
  )

  // A row that holds a document opens it: click, Enter or Space downloads the
  // first format. The chips inside stop their clicks from reaching the row.
  const documentRow = (report: ReportView, label: string, body: ReactElement): ReactElement => {
    const first = orderedFiles(report)[0]
    const open = (): void => {
      if (first && busy !== report.id) void download(report, first.format)
    }
    return <DocumentRow
      as='div'
      role='button'
      tabIndex={0}
      aria-label={label}
      onClick={open}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}>
      {body}
    </DocumentRow>
  }

  const renderStatementRow = (month: StatementMonthView): ReactElement => {
    const report = month.report ?? undefined
    const status = statementStatus(report)
    const ready = report?.status === 'ready'
    const pending = isInFlight(report) || preparing === month.periodKey
    const refused = isRefused(report)
    // Nothing to show yet: the row itself asks for the statement.
    if (!ready && !pending && !refused) {
      return <MonthRowButton
        key={month.periodKey}
        type='button'
        aria-label={`Get the statement for ${month.label}`}
        onClick={() => { void prepareMonth(month) }}>
        <MonthTile $muted>{monthAbbrev(month.periodKey)}</MonthTile>
        <RowMain>
          <RowTitleLine><Title>{month.label}</Title></RowTitleLine>
          {report?.status === 'failed' &&
            <RowSub><StatusDot $tone={status.tone} />{status.text}</RowSub>}
        </RowMain>
        <RowHint><span>{report?.status === 'failed' ? 'Try again' : 'Get statement'}</span><ChevronGlyph /></RowHint>
      </MonthRowButton>
    }
    const body = <>
      <MonthTile $muted={!ready}>{monthAbbrev(month.periodKey)}</MonthTile>
      <RowMain>
        <RowTitleLine>
          <Title>{month.label}</Title>
          {report?.isNew && <InlinePill $tone='green'>New</InlinePill>}
        </RowTitleLine>
        <RowSub><StatusDot $tone={status.tone} />{status.text}</RowSub>
      </RowMain>
      {pending
        ? <Preparing><Spinner style={{ width: 12, height: 12 }} /> Preparing</Preparing>
        : ready ? downloadChips(report) : <span />}
    </>
    return <Highlight
      key={month.periodKey}
      $on={!!report && report.id === openId}
      id={report ? `report-${report.id}` : undefined}>
      {ready
        ? documentRow(report, `Download the ${month.label} statement`, body)
        : <AccountRowStatic>{body}</AccountRowStatic>}
    </Highlight>
  }

  const renderReportRow = (report: ReportView): ReactElement => {
    const status = reportStatus(report)
    const body = <>
      <RowIconCircle $muted={report.status === 'expired'}><ReportKindGlyph kind={report.kind} /></RowIconCircle>
      <RowMain>
        <RowTitleLine>
          <Title>{report.title}</Title>
          {report.isNew && <InlinePill $tone='green'>New</InlinePill>}
        </RowTitleLine>
        <RowSub><StatusDot $tone={status.tone} />{report.periodLabel} · {status.text}</RowSub>
      </RowMain>
      {isInFlight(report)
        ? <Preparing><Spinner style={{ width: 12, height: 12 }} /> Preparing</Preparing>
        : report.status === 'ready'
          ? downloadChips(report)
          : isRefused(report)
            ? <span />
            : <RowAction type='button' onClick={() => openRequest(report.kind)}>
              {report.status === 'failed' ? retryLabel(report) : 'Request again'}<ChevronGlyph />
            </RowAction>}
    </>
    return <Highlight key={report.id} $on={report.id === openId} id={`report-${report.id}`}>
      {report.status === 'ready'
        ? documentRow(report, `Download ${report.title} for ${report.periodLabel}`, body)
        : <AccountRowStatic>{body}</AccountRowStatic>}
    </Highlight>
  }

  const latest = data?.statements[0]
  const latestReport = latest?.report ?? undefined
  const latestStatus = latest ? statementStatus(latestReport) : undefined
  const yearMonths = (data?.statements ?? [])
    .filter(s => s.periodKey.slice(0, 4) === year && s.periodKey !== latest?.periodKey)

  return <PageShell>
    <ListPage>
      <PageHeaderRow>
        <PageHeader
          title='Statements'
          subTitle='A statement for every month, prepared automatically, and reports for any period you choose.' />
        <PageActionRow>
          <PrimaryButton onClick={() => openRequest(undefined)}>Request a report</PrimaryButton>
        </PageActionRow>
      </PageHeaderRow>

      <ListContent>
        {loading && !data && <LoadingBar style={{ height: 96, borderRadius: 16, width: '100%' }} />}

        {data && !latest &&
          <Surface>
            <HeroMain>
              <HeroTitle>No statements yet</HeroTitle>
              <HeroSub>
                Your first monthly statement is prepared on the 1st after your first full month of activity.
              </HeroSub>
            </HeroMain>
          </Surface>}

        {data && latest && latestStatus &&
          <Highlight
            $on={!!latestReport && latestReport.id === openId}
            id={latestReport ? `report-${latestReport.id}` : undefined}>
            <Hero>
              <HeroHead>
                <MonthTile $size={52} $muted={latestReport?.status !== 'ready'}>{monthAbbrev(latest.periodKey)}</MonthTile>
                <HeroMain>
                  <HeroEyebrow>Latest statement</HeroEyebrow>
                  <RowTitleLine>
                    <HeroTitle>{latest.label}</HeroTitle>
                    {latestReport?.isNew && <InlinePill $tone='green'>New</InlinePill>}
                  </RowTitleLine>
                  <HeroSub><StatusDot $tone={latestStatus.tone} />{latestStatus.text}</HeroSub>
                </HeroMain>
              </HeroHead>
              <HeroActions>
                {isInFlight(latestReport) || preparing === latest.periodKey
                  ? <Preparing><Spinner style={{ width: 14, height: 14 }} /> Preparing your statement</Preparing>
                  : latestReport?.status === 'ready'
                    ? <>
                      <PrimaryButton
                        style={{ gap: 8 }}
                        disabled={busy === latestReport.id}
                        onClick={() => { void download(latestReport, 'pdf') }}>
                        <DownloadGlyph /> Download PDF
                      </PrimaryButton>
                      {latestReport.files.some(f => f.format === 'csv') &&
                        <SecondaryButton
                          style={{ gap: 8 }}
                          disabled={busy === latestReport.id}
                          onClick={() => { void download(latestReport, 'csv') }}>
                          CSV
                        </SecondaryButton>}
                    </>
                    : isRefused(latestReport)
                      ? null
                      : <PrimaryButton onClick={() => { void prepareMonth(latest) }}>
                        {latestReport?.status === 'failed' ? 'Try again' : 'Get statement'}
                      </PrimaryButton>}
              </HeroActions>
            </Hero>
          </Highlight>}

        {data && yearMonths.length > 0 &&
          <GroupStack>
            <SectionHead>
              <GroupLabel>Earlier statements</GroupLabel>
              {years.length > 1 && year &&
                <YearPicker years={years} year={year} onChange={setYear} />}
            </SectionHead>
            <GroupSurface>
              {yearMonths.map(renderStatementRow)}
            </GroupSurface>
          </GroupStack>}

        {data &&
          <GroupStack>
            <SectionHead>
              <GroupLabel>Reports you requested</GroupLabel>
            </SectionHead>
            {data.reports.length === 0
              ? <EmptyState style={{ minHeight: 96, flexDirection: 'column', gap: 10 }}>
                <span>Transactions, payments, payouts or fees for any period of up to a year, as a file.</span>
                <RowAction type='button' onClick={() => openRequest(undefined)}>
                  Request a report<ChevronGlyph />
                </RowAction>
              </EmptyState>
              : <GroupSurface>
                {data.reports.map(renderReportRow)}
              </GroupSurface>}
            {data.reports.length > 0 &&
              <HeroSub style={{ padding: '0 4px' }}>
                Requested reports stay available for {data.temporaryRetentionDays} days.
              </HeroSub>}
          </GroupStack>}
      </ListContent>
    </ListPage>

    <RequestReportDrawer
      visible={requestOpen}
      initialKind={initialKind}
      maxPeriodDays={data?.maxPeriodDays}
      onClose={closeRequest}
      onCreated={onCreated} />
  </PageShell>
})

export default Statements
